/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// One-time post-signup recovery-phrase prompt (V3 mnemonic flow).
// Displays the 12-word BIP39 mnemonic the host generated via
// `MessagingClient.generateMnemonicPhrase()` in a numbered, copyable
// grid and gates the upload button behind an explicit
// "I've written these down" confirmation.
//
// Display only — the host wires the SDK encrypt + upload + persist-
// completion logic through the props below so the screen stays free of
// platform crypto / network / storage dependencies.
//
// Unlike the legacy passphrase screen, the secret is generated
// asynchronously by the SDK and passed in as `mnemonic` — the host owns
// generation so the screen is platform-agnostic.

import { Check, Copy, ShieldCheck } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import { AuthHeader, AuthLayout, AuthPrimaryButton } from '../AuthLayout';

export interface RecoveryMnemonicSetupScreenProps {
  /**
   * The 12-word BIP39 phrase to display. The host generates it via
   * `MessagingClient.generateMnemonicPhrase()` and holds it stable for
   * the lifetime of the screen.
   */
  readonly mnemonic: string;
  /**
   * Called when the user confirms they've written the phrase down and
   * taps "Continue". Host is expected to:
   *   1. Build the V3 recovery blob from `mnemonic`.
   *   2. POST the ciphertext to `/v1/recovery/blobs`.
   *   3. Mark setup complete locally.
   * Rejection messages bubble up verbatim to the inline error slot.
   */
  readonly onComplete: () => Promise<void>;
  /**
   * Optional "skip for now" handler. When provided, a secondary link is
   * rendered below the primary button. Omit for mandatory onboarding.
   */
  readonly onSkip?: () => void;
  /** Host-supplied clipboard write (platform-native). */
  readonly onCopyToClipboard: (text: string) => Promise<void> | void;
}

export function RecoveryMnemonicSetupScreen({
  mnemonic,
  onComplete,
  onSkip,
  onCopyToClipboard,
}: RecoveryMnemonicSetupScreenProps): JSX.Element {
  const words = useMemo(
    () => mnemonic.trim().split(/\s+/).filter((w) => w.length > 0),
    [mnemonic],
  );

  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCopy = useCallback(async () => {
    try {
      await onCopyToClipboard(mnemonic);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(`Couldn't copy: ${(err as Error).message}`);
    }
  }, [onCopyToClipboard, mnemonic]);

  const handleContinue = useCallback(async () => {
    if (!acknowledged) return;
    setSubmitting(true);
    setError(null);
    try {
      await onComplete();
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  }, [acknowledged, onComplete]);

  return (
    <AuthLayout accessibilityLabel="recovery-setup">
      <AuthHeader
        title="Save your recovery phrase"
        subtitle="Write down these 12 words in order and keep them somewhere safe. If you lose every device, this phrase is the only way to recover your account. Ping cannot reset it for you."
      />

      <View className="flex-col gap-md">
        <MnemonicGrid words={words} />

        <View className="flex-row gap-sm">
          <SecondaryActionButton
            label={copied ? 'Copied' : 'Copy'}
            icon={copied ? Check : Copy}
            tone={copied ? 'success' : 'neutral'}
            onPress={() => {
              void handleCopy();
            }}
            accessibilityLabel="ping_recovery_copy"
          />
        </View>

        <AcknowledgeRow
          checked={acknowledged}
          onToggle={() => setAcknowledged((v) => !v)}
        />

        {error != null ? (
          <Text className="font-sans text-[12px] leading-[17px] text-light-accent-rose dark:text-dark-accent-rose">
            {error}
          </Text>
        ) : null}

        <View className="h-xs" />

        <AuthPrimaryButton
          onPress={() => {
            void handleContinue();
          }}
          disabled={!acknowledged || submitting}
          accessibilityLabel="ping_recovery_continue"
        >
          {submitting ? 'Saving backup…' : 'Continue'}
        </AuthPrimaryButton>

        {onSkip != null ? (
          <Pressable
            onPress={onSkip}
            accessibilityRole="link"
            accessibilityLabel="ping_recovery_skip"
            disabled={submitting}
            className="items-center pt-xs"
          >
            <Text className="font-sans font-normal text-[14px] leading-[20px] text-light-text-secondary dark:text-dark-text-secondary">
              Set up later
            </Text>
          </Pressable>
        ) : null}

        <ReassuranceFooter />
      </View>
    </AuthLayout>
  );
}

// ── Mnemonic word grid (numbered, two columns) ─────────────────────

interface MnemonicGridProps {
  readonly words: string[];
}

function MnemonicGrid({ words }: MnemonicGridProps): JSX.Element {
  return (
    <View
      accessibilityLabel="Recovery phrase"
      className="w-full rounded-[12px] border border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-panel dark:bg-dark-surface-panel px-md py-md"
    >
      <View className="flex-row flex-wrap">
        {words.map((word, i) => (
          <View
            // biome-ignore lint/suspicious/noArrayIndexKey: words are a fixed-length ordered list
            key={i}
            className="w-1/2 flex-row items-baseline gap-xs py-[6px] pr-sm"
          >
            <Text className="font-mono text-[13px] leading-[20px] tabular-nums w-6 text-right text-light-text-secondary dark:text-dark-text-secondary">
              {i + 1}.
            </Text>
            <Text
              selectable
              className="font-mono font-semibold text-[15px] leading-[20px] text-light-text-primary dark:text-dark-text-primary"
            >
              {word}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// ── Secondary action (Copy button) ─────────────────────────────────

interface SecondaryActionButtonProps {
  readonly label: string;
  readonly icon: typeof Copy;
  readonly tone: 'neutral' | 'success';
  readonly onPress: () => void;
  readonly accessibilityLabel: string;
}

function SecondaryActionButton({
  label,
  icon: Icon,
  tone,
  onPress,
  accessibilityLabel,
}: SecondaryActionButtonProps): JSX.Element {
  const bgClass =
    tone === 'success'
      ? 'bg-light-accent-emerald/[0.12] dark:bg-dark-accent-emerald/[0.12]'
      : 'bg-light-surface-elevated dark:bg-dark-surface-elevated';
  const textClass =
    tone === 'success'
      ? 'text-light-accent-emerald dark:text-dark-accent-emerald'
      : 'text-light-text-primary dark:text-dark-text-primary';
  const iconColor = tone === 'success' ? '#3CCB4F' : '#B5BAC3';
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      className={`flex-1 flex-row items-center justify-center gap-xs h-[40px] rounded-[8px] ${bgClass} active:opacity-70`}
    >
      <Icon size={16} strokeWidth={1.75} color={iconColor} />
      <Text className={`font-sans font-semibold text-[14px] leading-[20px] ${textClass}`}>
        {label}
      </Text>
    </Pressable>
  );
}

// ── Acknowledgement checkbox ───────────────────────────────────────

interface AcknowledgeRowProps {
  readonly checked: boolean;
  readonly onToggle: () => void;
}

function AcknowledgeRow({ checked, onToggle }: AcknowledgeRowProps): JSX.Element {
  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      accessibilityLabel="ping_recovery_ack"
      className="flex-row items-start gap-sm py-xs"
    >
      <View
        className={`w-5 h-5 rounded-[6px] border ${
          checked
            ? 'bg-light-accent-brand dark:bg-dark-accent-brand border-light-accent-brand dark:border-dark-accent-brand'
            : 'bg-transparent border-light-border-strong dark:border-dark-border-strong'
        } items-center justify-center mt-[2px]`}
      >
        {checked ? <Check size={14} strokeWidth={3} color="#FFFFFF" /> : null}
      </View>
      <Text className="flex-1 font-sans font-normal text-[14px] leading-[20px] text-light-text-primary dark:text-dark-text-primary">
        I've written these 12 words down somewhere safe.
      </Text>
    </Pressable>
  );
}

// ── Footer reassurance pill ────────────────────────────────────────

function ReassuranceFooter(): JSX.Element {
  return (
    <View className="flex-row items-center justify-center gap-xs mt-md">
      <ShieldCheck size={14} strokeWidth={1.75} color="#3CCB4F" />
      <Text className="font-sans font-normal text-[12px] leading-[17px] text-light-text-secondary dark:text-dark-text-secondary">
        End-to-end encrypted. Ping cannot read your recovery phrase.
      </Text>
    </View>
  );
}
